
import Component from 'vue-class-component';
import BaseModal from '@/jbi-shared/vue-components/BaseModal.vue';
import { Prop, Watch } from 'vue-property-decorator';
import dayjs from 'dayjs';
import { State } from 'vuex-class';
import { RootState } from '@/store/store';
import CitationForm from '@/views/DocumentEditor/components/tab/citations/CitationForm.vue';
import { Citation } from '@/store/modules/documents/types/citations.types';
import { ValidationObserver } from 'vee-validate';
import CitationUploader from '@/views/DocumentEditor/components/tab/citations/CitationUploader.vue';
import { ApiState } from '@/store/types/general.types';
import { get as _get } from 'lodash';
import { filterCitationsBySearch } from '@/utils/citation-reference.util';
import StagingCitationCard from '@/views/StagingArea/components/tab/StagingEditorCitationsTab_CitationCard.vue';
import CitationCard from '@/views/DocumentEditor/components/tab/EditorCitationsManagementTab_CitationCard.vue';
import { cloneDeep as _cloneDeep } from 'lodash';
import { mixins } from 'vue-class-component';
import { StagingEditorComputedValuesMixin } from '../../mixins/staging-editor-computed-values.mixin';
import { PendingCitationData } from '@/jbi-shared/types/document.types';
import { StagingEditorStateMixin } from '../../mixins/staging-editor-state.mixin';
import {
  getCitationReferenceNumber,
  transformAuthorArrayToString
} from '@/utils/citation-reference.util';
import {
  CreateCitationDto,
  UpdateCitationDto,
  ValidationObserverInstance,
  CitationImportResult,
  StagingCitationUnion,
  BaseModalInstance,
  PendingCitationUploaderInstance,
  CitationFormInstance
} from '@/jbi-shared/types/citation.dto';
import { VueComponentRef } from '@/types/CitationAuthor';
import {
  PendingCitationResponse,
  pendingCitationsApi
} from '@/api/pending-citations.api';
import { CitationsImportState } from '@/store/types/general.types';
import ArchiveCitationValidation from './ArchiveCitationValidation.vue';
import DeleteCitationValidation from './DeleteCitationValidation.vue';
import PendingCitationUploader from './PendingCitationUploader.vue';

@Component({
  // @ts-ignore
  components: {
    BaseModal,
    CitationForm,
    ValidationObserver,
    CitationUploader,
    CitationCard,
    StagingCitationCard,
    ArchiveCitationValidation,
    DeleteCitationValidation,
    PendingCitationUploader
  }
})
export default class StagingEditorCitationsTab extends mixins(
  StagingEditorComputedValuesMixin,
  StagingEditorStateMixin
) {
  @Prop(String) public citationSearchValue!: string;

  @State((state: RootState) => state.projects.apiState.createCitation.loading)
  public createCitationLoading!: boolean;

  @State((state: RootState) => state.projects.apiState.editCitation.loading)
  public editCitationLoading!: boolean;

  @State((state: RootState) => state.projects.apiState.uploadCitation)
  public uploadCitationApiState!: ApiState;

  @State((state: RootState) => state.projects.apiState.importCitations)
  public importCitationsState!: CitationsImportState;

  @State(
    (state: RootState) =>
      state.pendingCitations.apiState.getPendingCitations.loading
  )
  public pendingCitationsLoading!: boolean;

  @State(
    (state: RootState) =>
      state.pendingCitations.apiState.addPendingCitation.loading
  )
  public addPendingCitationLoading!: boolean;

  @Prop(Boolean)
  public isExpandedMiddleBar!: boolean;

  public archivedCitationNumber!: number;

  // New citation management state
  public addCitationLoading = false;
  public importCitationLoading = false;
  // REMOVED: newlyAddedCitations - now using store state instead
  public activeTab = 'active'; // Default to active tab
  public shouldShowModalFooter = false;

  mounted() {
    // Add a slight delay to ensure everything is initialized
    this.$nextTick(() => {
      this.forceRefreshCitations();
    });
  }

  activated() {
    // Component activated (keep-alive)
  }

  updated() {
    // Component updated
  }

  @Watch('documentDetail.id')
  async onDocumentChange(newDocumentId: number, oldDocumentId: number) {
    if (newDocumentId && newDocumentId !== oldDocumentId) {
      await this.forceRefreshCitations();
    }
  }

  @Watch('documentDetail')
  async onDocumentDetailChange(newDetail: any, oldDetail: any) {
    if (
      newDetail &&
      newDetail.id &&
      (!oldDetail || newDetail.id !== oldDetail?.id)
    ) {
      await this.forceRefreshCitations();
    }
  }

  @Watch('importCitationsState')
  async handleImportCitationProgress() {
    // This watcher is no longer needed since we're using PendingCitationUploader
    // The new component handles import logic directly
  }

  /**
   * Load newly added citations using store action
   * Now using state management instead of direct API calls
   */
  async loadNewlyAddedCitations() {
    if (!this.documentDetail?.id) {
      return;
    }

    try {
      // Use store action instead of direct API call for better state management
      await this.$store.dispatch(
        'pendingCitations/getPendingCitations',
        this.documentDetail.id
      );
    } catch (error) {
      console.error('Error loading citations:', error);
    }
  }

  /**
   * Refresh all citation data to ensure UI is up to date
   */
  async refreshAllCitations() {
    // Refresh newly added citations
    await this.loadNewlyAddedCitations();

    // Emit event to parent to refresh revision citations
    this.$emit('refreshRevisionCitations');
  }

  async forceRefreshCitations() {
    if (!this.documentDetail?.id) {
      return;
    }

    try {
      this.$store.commit(
        'pendingCitations/CLEAR_PENDING_CITATIONS',
        this.documentDetail.id
      );

      await this.loadNewlyAddedCitations();
    } catch (error) {
      console.error('Error in force refresh:', error);
    }
  }

  get newlyAddedCitations(): PendingCitationResponse[] {
    if (!this.documentDetail?.id) {
      return [];
    }

    const lastUpdated = this.$store.state.pendingCitations.lastUpdated;

    return this.$store.getters['pendingCitations/getCitationsForDocument'](
      this.documentDetail.id
    );
  }

  get citations(): Citation[] {
    // Return the original citation data without transforming the author field
    // The author transformation should only happen during display, not at the source level
    const citations: Citation[] = this.citationsOfRevision || [];
    return _cloneDeep(citations);
  }

  get referencedCitationIds(): number[] {
    const result = this.uniqUsedCitationIds.map((id) => parseInt(id));
    return result;
  }

  get totalCitationCount(): number {
    const previousCount = this.filteredPreviousCitations.length;
    const dirtyCount = this.filteredDirtyCitations.length;
    const newlyAddedCount = this.filteredNewlyAddedCitations.length;
    const archivedCount = this.filteredArchivedCitations.length;
    const archivedNewlyAddedCount = this.filteredArchivedNewlyAddedCitations
      .length;

    return (
      previousCount +
      dirtyCount +
      newlyAddedCount +
      archivedCount +
      archivedNewlyAddedCount
    );
  }

  get totalActiveCitationCount(): number {
    const newlyAddedCount = this.filteredNewlyAddedCitations.length;
    const dirtyCount = this.filteredDirtyCitations.length;
    const previousCount = this.filteredPreviousCitations.length;

    return newlyAddedCount + dirtyCount + previousCount;
  }

  get totalArchivedCitationCount(): number {
    return (
      this.filteredArchivedCitations.length +
      this.filteredArchivedNewlyAddedCitations.length
    );
  }

  get filteredCitations(): PendingCitationData[] {
    // Map Citation[] to PendingCitationData[]
    const filteredPreviousCitations: PendingCitationData[] = this.filteredPreviousCitations.map(
      (citation) => {
        return {
          id: citation.id.toString(),
          content: { ...citation.content }
        };
      }
    );
    return this.filteredDirtyCitations.concat(filteredPreviousCitations);
  }

  get filteredDirtyCitations(): PendingCitationData[] {
    if (this.activeCitations) {
      const existingCitationIds = new Set(
        this.citations.map((citation) => citation.id.toString())
      );

      // Get newly added citation IDs to exclude them from uploaded citations
      const newlyAddedIds = new Set<number>();
      if (this.documentDetail?.id) {
        const storeNewlyAdded = this.$store.getters[
          'pendingCitations/getCitationsForDocument'
        ](this.documentDetail.id).filter(
          (citation: PendingCitationResponse) => citation.status === 'ACTIVE'
        );
        storeNewlyAdded.forEach((citation: PendingCitationResponse) => {
          newlyAddedIds.add(citation.citationId);
        });
      }

      let filtered: PendingCitationData[];

      if (this.citationSearchValue) {
        const data = this.activeCitations;
        filtered = data
          .filter((dirtyCitation) => {
            const notExisting = !existingCitationIds.has(
              dirtyCitation.id.toString()
            );
            const notNewlyAdded = !newlyAddedIds.has(Number(dirtyCitation.id));
            return notExisting && notNewlyAdded;
          })
          .filter((citation) => {
            const citationText =
              citation.content.plainText?.toLowerCase() || '';
            return citationText.includes(
              this.citationSearchValue.toLowerCase()
            );
          });
      } else {
        filtered = this.activeCitations.filter((dirtyCitation) => {
          const notExisting = !existingCitationIds.has(
            dirtyCitation.id.toString()
          );
          const notNewlyAdded = !newlyAddedIds.has(Number(dirtyCitation.id));
          return notExisting && notNewlyAdded;
        });
      }

      return filtered;
    } else {
      return [];
    }
  }

  get filteredPreviousCitations(): Citation[] {
    const data: Citation[] = this.citations;

    // Get IDs of archived citations to exclude them from active display
    const archivedCitationIds = this.archivedCitations.map((citation) =>
      citation.id.toString()
    );

    return (
      data
        // filter out archived citations
        .filter((citation) => {
          return !archivedCitationIds.includes(citation.id.toString());
        })
        // Use modified version from activeCitations if it exists, otherwise use original
        .map((citation) => {
          const modifiedCitation = this.activeCitations.find(
            (activeCitation) =>
              activeCitation.id.toString() === citation.id.toString()
          );

          if (modifiedCitation) {
            // Use the modified version from activeCitations
            return {
              ...citation,
              content: modifiedCitation.content
            };
          }

          // Use the original citation
          return citation;
        })
        // filter based on search
        .filter((citation) => {
          return filterCitationsBySearch(citation, this.citationSearchValue);
        })
        // format date
        .map((c) => ({
          ...c,
          issueDate: this.getYearFromDate(c.content.issueDate)
        }))
    );
  }

  get hasAnyCitations(): boolean {
    const totalCount = this.totalCitationCount;
    const archivedCount = this.filteredArchivedCitations.length;
    return totalCount > 0 || archivedCount > 0;
  }

  get hasFilteredCitations(): boolean {
    return (
      this.filteredNewlyAddedCitations.length > 0 ||
      this.filteredDirtyCitations.length > 0 ||
      this.filteredPreviousCitations.length > 0 ||
      this.filteredArchivedCitations.length > 0
    );
  }

  get filteredNewlyAddedCitations(): PendingCitationResponse[] {
    if (!this.citationSearchValue) {
      return this.newlyAddedCitations.filter(
        (citation) => citation.status === 'ACTIVE'
      );
    }

    return this.newlyAddedCitations
      .filter((citation) => citation.status === 'ACTIVE')
      .filter((citation) => {
        const content = citation.citation.content;
        const author = transformAuthorArrayToString(
          _get(content, 'author', '')
        ).toLowerCase();
        const title = String(_get(content, 'title', '')).toLowerCase();
        const plainText = _get(content, 'plainText', '').toLowerCase();
        const searchTerm = this.citationSearchValue.toLowerCase();

        return (
          author.includes(searchTerm) ||
          title.includes(searchTerm) ||
          plainText.includes(searchTerm)
        );
      });
  }

  get filteredActiveCitations(): (PendingCitationData | Citation)[] {
    return [...this.filteredDirtyCitations, ...this.filteredPreviousCitations];
  }

  get filteredArchivedNewlyAddedCitations(): PendingCitationResponse[] {
    if (!this.citationSearchValue) {
      return this.newlyAddedCitations.filter(
        (citation) => citation.status === 'ARCHIVED'
      );
    }

    return this.newlyAddedCitations
      .filter((citation) => citation.status === 'ARCHIVED')
      .filter((citation) => {
        const content = citation.citation.content;
        const author = transformAuthorArrayToString(
          _get(content, 'author', '')
        ).toLowerCase();
        const title = String(_get(content, 'title', '')).toLowerCase();
        const plainText = _get(content, 'plainText', '').toLowerCase();
        const searchTerm = this.citationSearchValue.toLowerCase();

        return (
          author.includes(searchTerm) ||
          title.includes(searchTerm) ||
          plainText.includes(searchTerm)
        );
      });
  }

  get filteredArchivedCitations(): PendingCitationData[] {
    // Simply return the archivedCitations array - it contains the most up-to-date data
    // including any edits that have been made to archived citations
    let citationsArchived = [...this.archivedCitations];

    // Apply search filter if needed
    if (this.citationSearchValue) {
      return citationsArchived.filter((citation) => {
        const citationText = citation.content.plainText?.toLowerCase() || '';
        const author = transformAuthorArrayToString(
          _get(citation, 'content.author', '')
        ).toLowerCase();
        const title = String(_get(citation, 'content.title', '')).toLowerCase();
        return (
          author.includes(this.citationSearchValue.toLowerCase()) ||
          title.includes(this.citationSearchValue.toLowerCase()) ||
          citationText.includes(this.citationSearchValue.toLowerCase())
        );
      });
    }

    return citationsArchived;
  }

  public expandCitationTab() {
    this.$emit('expandMiddleBar');
  }

  public citationReferenceNumber(
    citation: Citation | PendingCitationData | PendingCitationResponse
  ): string | undefined {
    let targetCitation;

    if ('citationId' in citation) {
      // PendingCitationResponse
      targetCitation = this.convertNewlyAddedToDisplayFormat(citation);
    } else {
      targetCitation = citation;
    }

    const citationReferenceNumber = getCitationReferenceNumber(
      targetCitation,
      this.dirtyReferences?.citationIds || []
    );
    if (!citationReferenceNumber) {
      return;
    }
    return citationReferenceNumber + '.';
  }

  /**
   * Convert newly added citation to display format
   */
  convertNewlyAddedToDisplayFormat(citation: PendingCitationResponse) {
    return {
      id: citation.citationId,
      content: {
        ...citation.citation.content,
        author: transformAuthorArrayToString(citation.citation.content.author)
      },
      createdAt: citation.citation.createdAt
    };
  }

  /**
   * Convert previous citation to display format
   */
  convertPreviousCitationToDisplayFormat(
    citation: Citation
  ): PendingCitationData {
    const converted = {
      id: citation.id.toString(),
      content: {
        ...citation.content,
        author: transformAuthorArrayToString(citation.content.author)
      }
    };

    return converted;
  }

  /**
   * Get year from date string
   */
  getYearFromDate(date: string | number): string {
    if (String(date).length === 4) {
      return String(date);
    }
    return dayjs(date).format('YYYY');
  }

  async handleRetryUpload() {
    if (this.$refs.pendingCitationUploader) {
      await (this.$refs.pendingCitationUploader as VueComponentRef<
        PendingCitationUploaderInstance
      >).handleRetryUpload();
    }
  }

  /**
   * Update a citation with edited data from the form
   */
  updateCitation(editedCitation: StagingCitationUnion) {
    // Check if this is a newly added citation
    const isNewlyAdded = this.newlyAddedCitations.some((c) => {
      return c.citationId.toString() === editedCitation.id.toString();
    });

    // Check if this citation is in archivedCitations
    const isInArchived = this.archivedCitations.some(
      (c) => c.id.toString() === editedCitation.id.toString()
    );

    // Check if this citation is in activeCitations
    const isInActive = this.activeCitations.some(
      (c) => c.id.toString() === editedCitation.id.toString()
    );

    if (isNewlyAdded) {
      // For newly added citations, use the edited data from the form
      this.handleEditNewlyAddedCitation(editedCitation);
    } else if (isInArchived) {
      // For archived citations, update them in the archived list
      this.handleEditArchivedCitation(editedCitation);
    } else if (isInActive) {
      // For uploaded citations already in activeCitations
      this.handleEditUploadedCitation(editedCitation);
    } else {
      // For previous citations from the revision
      this.handleEditPreviousCitation(editedCitation);
    }
  }

  /**
   * Handle editing a newly added citation
   */
  async handleEditNewlyAddedCitation(citation: StagingCitationUnion) {
    if (!this.documentDetail?.id) {
      return;
    }

    try {
      const citationId =
        typeof citation.id === 'string' ? parseInt(citation.id) : citation.id;

      // Convert CitationContent to UpdateCitationDto format
      // Extract author separately to handle type conversion, then spread the rest
      const { author, year, journal, ...restContent } = citation.content;

      const updateData: UpdateCitationDto = {
        ...restContent,
        id: citation.id.toString()
      };

      // Handle author data properly - it can be either a string or an array
      if (author) {
        if (Array.isArray(author)) {
          // Author is already in the correct CitationAuthor[] format
          updateData.author = author;
        } else if (typeof author === 'string' && author.trim()) {
          // Author is a string, convert to CitationAuthor[] format
          updateData.author = author.split(',').map((name) => {
            const trimmedName = name.trim();
            const parts = trimmedName.split(' ');
            if (parts.length === 1) {
              return { given: parts[0], family: '' };
            } else {
              const family = parts[parts.length - 1];
              const given = parts.slice(0, -1).join(' ');
              return { given, family };
            }
          });
        }
      }

      // FIXED: Use store action instead of direct API call
      const updatedCitation = await this.$store.dispatch(
        'pendingCitations/updatePendingCitation',
        {
          documentId: this.documentDetail.id,
          citationId,
          citationData: updateData
        }
      );

      // Show success toast
      this.$buefy.toast.open({
        message: 'Citation Edited Successfully',
        type: 'is-dark',
        duration: 2000
      });

      this.$emit('citationUpdated', updatedCitation);

      // DO NOT save automatically - the API call already updated the backend
    } catch (error) {
      // Handle error appropriately
    }
  }

  /**
   * Save only citations without triggering full document refresh
   */
  async saveCitationsOnly() {
    if (!this.documentDetail?.id || !this.documentDetail?.documentId) {
      this.$emit('saveDocument');
      return;
    }

    try {
      // Special case exception to use admin API directly, do not copy this flow.
      // Use the admin API directly to avoid the Vuex action that causes refetch
      // Include ALL current document data to prevent data loss
      const payload = {
        pendingDocumentId: this.documentDetail.id,
        projectId: this.documentDetail.projectId,
        documentId: this.documentDetail.documentId,
        // Include all current document data to prevent wiping out other fields
        documentTitle:
          this.dirtyDocumentTitle || this.documentDetail.content?.documentTitle,
        searchDate:
          this.dirtySearchDate || this.documentDetail.content?.searchDate,
        authorSection:
          this.dirtyAuthors || this.documentDetail.content?.authorSection,
        textSections:
          this.dirtyTextSections || this.documentDetail.content?.textSections,
        bprSection: this.dirtyBprs || this.documentDetail.content?.bprSection,
        criterionSection:
          this.dirtyCriterions || this.documentDetail.content?.criterionSection,
        referenceSection:
          this.dirtyReferences || this.documentDetail.content?.referenceSection,
        relatedDocSection:
          this.dirtyRelatedDocs ||
          this.documentDetail.content?.relatedDocSection,
        ohsSection: this.dirtyOhs || this.documentDetail.content?.ohsSection,
        tags: this.documentDetail.content?.tags,
        tdrUri: this.tdrUri || this.documentDetail.content?.tdrUri,
        isValid: this.documentDetail.isValid,
        // Only update citations
        activeCitations: this.activeCitations,
        archivedCitations: this.archivedCitations
      };

      // Import the admin API
      const { adminApi } = await import('@/api/admin.api');
      await adminApi.updatePendingDocument(payload);

      // Show a success message
      this.$buefy.toast.open({
        message: 'Citation Edited successfully',
        type: 'is-dark',
        duration: 2000
      });
    } catch (error) {
      // Fall back to full document save if direct API fails
      this.$emit('saveDocument');
    }
  }

  /**
   * Handle editing an uploaded citation (already in activeCitations)
   */
  handleEditUploadedCitation(citation: StagingCitationUnion) {
    // Uploaded citations are stored locally in activeCitations and need document save
    // They cannot be updated via pending citations API since they're not "newly added" citations

    const index = this.activeCitations.findIndex(
      (c) => c.id.toString() === citation.id.toString()
    );

    if (index !== -1) {
      const updatedCitation = {
        ...this.activeCitations[index],
        content: {
          ...citation.content,
          // Keep author as an array of objects, do not convert to string
          author: citation.content.author
        }
      };

      this.activeCitations = [
        ...this.activeCitations.slice(0, index),
        updatedCitation,
        ...this.activeCitations.slice(index + 1)
      ];

      // Save citations only
      this.saveCitationsOnly();
    }
  }

  /**
   * Handle editing a previous citation
   */
  async handleEditPreviousCitation(citation: StagingCitationUnion) {
    // Previous citations from the revision cannot be updated via pending citations API
    // They need to be tracked in activeCitations and saved via document save
    // But we can make this save without page refresh by using a lighter save method

    const existingIndex = this.activeCitations.findIndex(
      (c) => c.id.toString() === citation.id.toString()
    );

    const updatedCitation = {
      id: citation.id.toString(),
      content: {
        ...citation.content,
        // Keep author as an array of objects, do not convert to string
        author: citation.content.author
      }
    };

    if (existingIndex !== -1) {
      this.activeCitations = [
        ...this.activeCitations.slice(0, existingIndex),
        updatedCitation,
        ...this.activeCitations.slice(existingIndex + 1)
      ];
    } else {
      this.activeCitations = [...this.activeCitations, updatedCitation];
    }

    // Save only citations without full document refresh
    this.saveCitationsOnly();
  }

  /**
   * Handle editing an archived citation
   */
  handleEditArchivedCitation(citation: StagingCitationUnion) {
    // Archived citations are stored locally and need document save
    // They cannot be updated via pending citations API

    const index = this.archivedCitations.findIndex(
      (c) => c.id.toString() === citation.id.toString()
    );

    if (index !== -1) {
      const updatedCitation = {
        ...this.archivedCitations[index],
        content: {
          ...citation.content,
          // Keep author as an array of objects, do not convert to string
          author: citation.content.author
        }
      };

      this.archivedCitations = [
        ...this.archivedCitations.slice(0, index),
        updatedCitation,
        ...this.archivedCitations.slice(index + 1)
      ];

      // Show success toast
      this.$buefy.toast.open({
        message: 'Citation Edited Successfully',
        type: 'is-dark',
        duration: 2000
      });

      // Save citations only
      this.saveCitationsOnly();
    }
  }

  async handleFormSubmit(validator: ValidationObserverInstance) {
    const validated = await validator.validate();
    if (validated === false) return;
    this.handleCitationSubmit();
  }

  async handleCitationSubmit() {
    // This will be called when the form is submitted
    // Instead of calling the form's submit, we'll get the citation data and handle it ourselves
    if (this.$refs.CitationFormForCreate) {
      try {
        const citationFields = (this.$refs
          .CitationFormForCreate as VueComponentRef<
          CitationFormInstance
        >).transformFieldToCitation();

        // Convert CitationFormFields to CreateCitationDto format
        const citationData: CreateCitationDto = {
          ...citationFields,
          type: citationFields.type || undefined
        };

        // Call our citation added handler directly
        await this.handleCitationAdded(citationData);

        // Close the modal
        this.handleCitationModalClose();
      } catch (error) {
        // Handle error silently or use proper error handling
      }
    }
  }

  handleCitationModalClose() {
    if (this.$refs.addCitationModal) {
      (this.$refs.addCitationModal as VueComponentRef<
        BaseModalInstance
      >).isOpen = false;
    }
  }

  async handleCitationCreated(citation: CreateCitationDto) {
    // Close the modal first
    this.handleCitationModalClose();

    // Call our existing citation added handler
    await this.handleCitationAdded(citation);
  }

  async handleCitationSubmitted(citation: CreateCitationDto) {
    // Close the modal first
    this.handleCitationModalClose();

    // Call our existing citation added handler
    await this.handleCitationAdded(citation);
  }

  async handleCitationAdded(citation: CreateCitationDto) {
    if (!this.documentDetail?.id) {
      return;
    }

    try {
      const newCitation = await this.$store.dispatch(
        'pendingCitations/addPendingCitation',
        {
          documentId: this.documentDetail.id,
          citationData: citation
        }
      );

      // Show success toast
      this.$buefy.toast.open({
        message: 'Citation Created Successfully',
        type: 'is-dark',
        duration: 2000
      });

      // Emit event for parent component
      this.$emit('citationAdded', newCitation);
    } catch (error) {
      // Handle error silently or use proper error handling
    }
  }

  /**
   * Archive a newly added citation
   */
  async archiveNewlyAddedCitation(citation: StagingCitationUnion) {
    const citationId =
      typeof citation.id === 'string' ? parseInt(citation.id) : citation.id;
    const isReferenced = this.referencedCitationIds.includes(citationId);

    if (!this.documentDetail?.id) {
      return;
    }

    if (isReferenced) {
      // Show the warning modal using Buefy
      this.$buefy.modal.open({
        parent: this,
        component: ArchiveCitationValidation,
        hasModalCard: true,
        trapFocus: true,
        events: {
          'archive-anyway': async () => {
            try {
              const updatedCitationMap = await this.$store.dispatch(
                'pendingCitations/archivePendingCitation',
                {
                  documentId: this.documentDetail!.id,
                  citationId
                }
              );

              this.$emit('citationArchived', updatedCitationMap);
            } catch (error) {
              // Handle error silently or use proper error handling
            }
          }
        }
      });

      return;
    }

    try {
      const updatedCitationMap = await this.$store.dispatch(
        'pendingCitations/archivePendingCitation',
        {
          documentId: this.documentDetail.id,
          citationId
        }
      );

      this.$emit('citationArchived', updatedCitationMap);
    } catch (error) {
      // Handle error silently or use proper error handling
    }
  }

  /**
   * Delete a newly added citation
   */
  async deleteNewlyAddedCitation(citation: StagingCitationUnion) {
    const citationId =
      typeof citation.id === 'string' ? parseInt(citation.id) : citation.id;
    const isReferenced = this.referencedCitationIds.includes(citationId);

    if (!this.documentDetail?.id) {
      return;
    }

    if (isReferenced) {
      this.$buefy.modal.open({
        parent: this,
        component: DeleteCitationValidation,
        hasModalCard: true,
        trapFocus: true
      });
      return;
    }

    try {
      await this.$store.dispatch('pendingCitations/deletePendingCitation', {
        documentId: this.documentDetail.id,
        citationId
      });

      this.$emit('citationDeleted', citation);
    } catch (error) {
      // Handle error silently or use proper error handling
    }
  }

  /**
   * Restore an archived citation
   */
  async restoreArchivedCitation(citation: StagingCitationUnion) {
    if (!this.documentDetail?.id) {
      return;
    }

    try {
      const citationId =
        typeof citation.id === 'string' ? parseInt(citation.id) : citation.id;
      const pendingCitation = this.newlyAddedCitations.find(
        (c) => c.citationId === citationId
      );

      if (pendingCitation) {
        await this.$store.dispatch('pendingCitations/restorePendingCitation', {
          documentId: this.documentDetail.id,
          citationId
        });

        this.$emit('citationRestored', pendingCitation);
      }
    } catch (error) {
      // Handle error - could show toast or modal
    }
  }

  /**
   * Archive a previous citation
   */
  async archivePreviousCitation(citation: StagingCitationUnion) {
    if (!this.documentDetail?.id) {
      return;
    }

    // Check if citation is referenced in the document
    const citationIdStr = citation.id.toString();
    const isReferenced = this.usedCitationIds.includes(citationIdStr);

    if (!isReferenced) {
      await this.archivePreviousCitationToBackend(citation);
    } else {
      // Citation is referenced, show warning modal
      this.$buefy.modal.open({
        parent: this,
        component: ArchiveCitationValidation,
        hasModalCard: true,
        trapFocus: true,
        events: {
          'archive-anyway': async () => {
            await this.archivePreviousCitationToBackend(citation);
          }
        }
      });
    }
  }

  /**
   * Archive a previous citation to the backend and update local state
   */
  async archivePreviousCitationToBackend(citation: StagingCitationUnion) {
    try {
      // Convert the previous citation to PendingCitationData format
      const pendingCitationData: PendingCitationData = {
        id: citation.id.toString(),
        content: {
          ...citation.content,
          author: transformAuthorArrayToString(citation.content.author)
        }
      };

      // Add to archivedCitations array so it appears in the archived tab
      // Use setter to avoid Vuex mutation errors
      this.archivedCitations = [...this.archivedCitations, pendingCitationData];

      // DO NOT save the document automatically - wait for user to click save button
    } catch (error) {
      // Show error message to user
      this.$buefy.toast.open({
        message: 'Failed to archive citation. Please try again.',
        type: 'is-danger',
        duration: 2000
      });
    }
  }

  /**
   * Unarchive a newly added citation
   */
  async unarchiveNewlyAddedCitation(citation: StagingCitationUnion) {
    if (!this.documentDetail?.id) {
      return;
    }

    try {
      const citationId =
        typeof citation.id === 'string' ? parseInt(citation.id) : citation.id;

      await this.$store.dispatch('pendingCitations/restorePendingCitation', {
        documentId: this.documentDetail.id,
        citationId
      });

      this.$emit('citationUnarchived', citation);
    } catch (error) {
      // Handle error silently or use proper error handling
    }
  }

  /**
   * Unarchive a previous citation
   */
  async unarchivePreviousCitation(citation: StagingCitationUnion) {
    const citationObject = this.archivedCitations.find(
      (data) => data.id.toString() === citation.id.toString()
    );

    if (citationObject) {
      // Remove from archived citations using setter to avoid Vuex mutation errors
      this.archivedCitations = this.archivedCitations.filter(
        (el) => el.id !== citationObject.id
      );

      // DO NOT save the document automatically - wait for user to click save button
    }
  }

  /**
   * Delete an uploaded citation
   */
  async deleteUploadedCitation(citation: StagingCitationUnion) {
    // Uploaded citations are managed locally in the pending document content
    // Remove from activeCitations using setter to avoid Vuex mutation errors
    this.activeCitations = this.activeCitations.filter(
      (el) => el.id !== citation.id
    );

    // DO NOT save the document automatically - wait for user to click save button
  }

  async handlePendingImportSuccess(importResult: CitationImportResult) {
    try {
      // Import operations are handled by PendingCitationUploader which uses store actions
      // No need for manual refresh - store mutations will trigger reactivity automatically

      // Emit event for any parent components that might need to react
      this.$emit('refreshNewlyAddedCitations');

      // Only close the import modal if there were no failures (complete success)
      if (importResult.failed === 0 && this.$refs.importCitationModal) {
        (this.$refs.importCitationModal as VueComponentRef<
          BaseModalInstance
        >).isOpen = false;
      }
    } catch (error) {
      console.error('Error in import success handler:', error);
    }
  }

  handleFooterVisibilityChange(shouldShow: boolean) {
    this.shouldShowModalFooter = shouldShow;
  }
}
